import { z } from "zod";

export const ShipmentArrangementSchema = z.enum(["NOT_RECORDED", "SELF_ARRANGED", "CUSTOMER_PICKUP", "SUPPLIER_DELIVERY"]);

export type ShipmentArrangement = z.infer<typeof ShipmentArrangementSchema>;
export type ShipmentArrangementContext = "salesOrder" | "purchaseOrder" | "directShip";
export type ShipmentArrangementFlags = {
  customerPickup: boolean | null;
  supplierDelivery: boolean | null;
};

export const SHIPMENT_ARRANGEMENT_LABELS: Record<ShipmentArrangement, string> = {
  NOT_RECORDED: "Not recorded",
  SELF_ARRANGED: "Us",
  CUSTOMER_PICKUP: "Customer",
  SUPPLIER_DELIVERY: "Supplier",
};

export function isShipmentArrangementSupported(arrangement: ShipmentArrangement, context: ShipmentArrangementContext): boolean {
  if (context === "salesOrder") {
    return arrangement !== "SUPPLIER_DELIVERY";
  }
  if (context === "purchaseOrder") {
    return arrangement !== "CUSTOMER_PICKUP";
  }
  return true;
}

export function getShipmentArrangementOptions(context: ShipmentArrangementContext) {
  return ShipmentArrangementSchema.options
    .filter(arrangement => arrangement !== "NOT_RECORDED" && isShipmentArrangementSupported(arrangement, context))
    .map(value => ({ value, label: SHIPMENT_ARRANGEMENT_LABELS[value] }));
}

/** Conflicting saved flags throw; display callers must show the conflict instead of hiding it or crashing the page. */
export function deriveShipmentArrangement({ customerPickup, supplierDelivery }: ShipmentArrangementFlags): ShipmentArrangement {
  if (customerPickup && supplierDelivery) {
    throw new Error("Customer pickup and supplier delivery cannot both be enabled.");
  }
  if (customerPickup) {
    return "CUSTOMER_PICKUP";
  }
  if (supplierDelivery) {
    return "SUPPLIER_DELIVERY";
  }
  return customerPickup === false && supplierDelivery === false ? "SELF_ARRANGED" : "NOT_RECORDED";
}

export function projectShipmentArrangement(arrangement: ShipmentArrangement): ShipmentArrangementFlags {
  if (arrangement === "NOT_RECORDED") {
    return { customerPickup: null, supplierDelivery: null };
  }
  return {
    customerPickup: arrangement === "CUSTOMER_PICKUP",
    supplierDelivery: arrangement === "SUPPLIER_DELIVERY",
  };
}

/** Only for create/copy defaults and form type changes; persisted edits must validate the explicit choice. */
export function normalizeSalesOrderShipmentArrangement(
  arrangement: ShipmentArrangement | undefined,
  isDirectShip: boolean,
): ShipmentArrangement {
  if (!isDirectShip && arrangement === "SUPPLIER_DELIVERY") {
    return "NOT_RECORDED";
  }
  return arrangement ?? "NOT_RECORDED";
}
